import React, { useState, useEffect } from 'react';
import { X, Moon, Minus, Plus, Underline, AlignJustify, RotateCcw, Pause, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAnimations } from '@/contexts/AnimationContext';
import { motion, AnimatePresence } from 'framer-motion';

interface AccessibilityMenuProps {
    isOpen: boolean;
    onClose: () => void;
}

const AccessibilityMenu: React.FC<AccessibilityMenuProps> = ({ isOpen, onClose }) => {
    const [fontSize, setFontSize] = useState(100);
    const [highContrast, setHighContrast] = useState(false);
    const [highlightLinks, setHighlightLinks] = useState(false);
    const [readableFont, setReadableFont] = useState(false);
    
    const { animationsEnabled, toggleAnimations } = useAnimations();

    // Handle font size changes
    const increaseFontSize = () => {
        if (fontSize < 150) {
            const newSize = fontSize + 10;
            setFontSize(newSize);
            document.documentElement.style.fontSize = `${newSize}%`;
        }
    };

    const decreaseFontSize = () => {
        if (fontSize > 90) {
            const newSize = fontSize - 10;
            setFontSize(newSize);
            document.documentElement.style.fontSize = `${newSize}%`;
        }
    };

    // Handle contrast changes
    const toggleHighContrast = () => {
        setHighContrast(!highContrast);
        document.documentElement.classList.toggle('high-contrast');
    };

    // Handle link highlighting
    const toggleHighlightLinks = () => {
        setHighlightLinks(!highlightLinks);
        document.documentElement.classList.toggle('highlight-links');
    };

    // Handle readable font
    const toggleReadableFont = () => {
        setReadableFont(!readableFont);
        document.documentElement.classList.toggle('readable-font');
    };

    // Reset all accessibility settings
    const resetSettings = () => {
        setFontSize(100);
        setHighContrast(false);
        setHighlightLinks(false);
        setReadableFont(false);
        
        // Reset animations to enabled state
        if (!animationsEnabled) {
            toggleAnimations();
        }
        
        document.documentElement.style.fontSize = '100%';
        document.documentElement.classList.remove('high-contrast');
        document.documentElement.classList.remove('highlight-links');
        document.documentElement.classList.remove('readable-font');
    };

    // Close the menu with Escape key
    useEffect(() => {
        const handleEscapeKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        
        window.addEventListener('keydown', handleEscapeKey);
        return () => window.removeEventListener('keydown', handleEscapeKey);
    }, [isOpen, onClose]);

    // Prevent scroll when menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const menuVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.3,
                ease: [0.22, 1, 0.36, 1],
                staggerChildren: 0.1
            }
        },
        exit: {
            opacity: 0,
            scale: 0.95,
            transition: {
                duration: 0.2,
                ease: [0.22, 1, 0.36, 1]
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.3,
                ease: [0.22, 1, 0.36, 1]
            }
        }
    };

    return (
        <AnimatePresence>
            <motion.div 
                className="fixed inset-0 bg-background/95 z-50 overflow-auto"
                aria-modal="true"
                role="dialog"
                aria-label="Accessibility options"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={menuVariants}
            >
                <motion.div 
                    className="max-w-md mx-auto p-5 my-10 bg-card border border-border rounded-lg shadow-lg"
                    variants={itemVariants}
                >
                    <motion.div 
                        className="flex justify-between items-center mb-6"
                        variants={itemVariants}
                    >
                        <h2 className="text-2xl font-bold">Accessibility Options</h2>
                        <motion.button 
                            onClick={onClose} 
                            className="p-2 rounded-full hover:bg-muted"
                            aria-label="Close accessibility menu"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <X className="h-6 w-6" />
                        </motion.button>
                    </motion.div>
                    
                    <motion.h3 
                        className="text-xl font-semibold mb-4"
                        variants={itemVariants}
                    >
                        Display Settings
                    </motion.h3>
                    
                    <motion.div 
                        className="grid grid-cols-2 gap-4 mb-8"
                        variants={itemVariants}
                    >
                        <motion.button 
                            onClick={toggleHighContrast}
                            className={cn(
                                "flex flex-col items-center p-4 border rounded-lg hover:border-primary transition-colors",
                                highContrast && "border-primary bg-primary/10"
                            )}
                            aria-pressed={highContrast}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Moon className="h-10 w-10 mb-2" />
                            <span>High Contrast</span>
                        </motion.button>
                        
                        <motion.button 
                            onClick={resetSettings}
                            className="flex flex-col items-center p-4 border rounded-lg hover:border-primary transition-colors"
                            aria-label="Reset all accessibility settings"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <RotateCcw className="h-10 w-10 mb-2" />
                            <span>Reset All</span>
                        </motion.button>
                    </motion.div>
                    
                    <motion.h3 
                        className="text-xl font-semibold mb-4"
                        variants={itemVariants}
                    >
                        Content Settings
                    </motion.h3>
                    
                    <motion.div 
                        className="grid grid-cols-2 gap-4 mb-8"
                        variants={itemVariants}
                    >
                        <motion.button 
                            onClick={decreaseFontSize}
                            className="flex flex-col items-center p-4 border rounded-lg hover:border-primary transition-colors"
                            aria-label="Decrease text size"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Minus className="h-10 w-10 mb-2" />
                            <span>Decrease Text</span>
                        </motion.button>
                        
                        <motion.button 
                            onClick={increaseFontSize}
                            className="flex flex-col items-center p-4 border rounded-lg hover:border-primary transition-colors"
                            aria-label="Increase text size"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Plus className="h-10 w-10 mb-2" />
                            <span>Increase Text</span>
                        </motion.button>
                        
                        <motion.button 
                            onClick={toggleHighlightLinks}
                            className={cn(
                                "flex flex-col items-center p-4 border rounded-lg hover:border-primary transition-colors",
                                highlightLinks && "border-primary bg-primary/10"
                            )}
                            aria-pressed={highlightLinks}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Underline className="h-10 w-10 mb-2" />
                            <span>Highlight Links</span>
                        </motion.button>
                        
                        <motion.button 
                            onClick={toggleReadableFont}
                            className={cn(
                                "flex flex-col items-center p-4 border rounded-lg hover:border-primary transition-colors",
                                readableFont && "border-primary bg-primary/10"
                            )}
                            aria-pressed={readableFont}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <AlignJustify className="h-10 w-10 mb-2" />
                            <span>Readable Font</span>
                        </motion.button>
                    </motion.div>
                    
                    <motion.div 
                        className="grid grid-cols-1 gap-4"
                        variants={itemVariants}
                    >
                        <motion.button 
                            onClick={toggleAnimations}
                            className={cn(
                                "flex flex-col items-center p-4 border rounded-lg hover:border-primary transition-colors",
                                !animationsEnabled && "border-primary bg-primary/10"
                            )}
                            aria-pressed={!animationsEnabled}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {animationsEnabled ? (
                                <>
                                    <Pause className="h-10 w-10 mb-2" />
                                    <span>Stop Animations</span>
                                </>
                            ) : (
                                <>
                                    <Play className="h-10 w-10 mb-2" />
                                    <span>Enable Animations</span>
                                </>
                            )}
                        </motion.button>
                    </motion.div>
                    
                    <motion.div 
                        className="mt-8 text-center"
                        variants={itemVariants}
                    >
                        <motion.a 
                            href="/accessibility" 
                            className="text-primary underline"
                            onClick={() => onClose()}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            View Full Accessibility Statement
                        </motion.a>
                    </motion.div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default AccessibilityMenu; 